import { Trans } from '@lingui/react'
import classNames from 'classnames'
import { bool, number, oneOf, shape, string } from 'prop-types'

import {
  AVAILABLE_PRESCRIPTION_TYPES,
  AVAILABLE_PRODUCT_TEMPLATES,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
} from 'shared/consts/productTemplates'
import {
  RELEASE_TOGGLE_VARIANT_ATTTRIBUTES_ENABLED,
  VARIANT_ATTRIBUTES,
} from 'shared/experiments/consts'
import { isExperimentVariableTrue } from 'shared/experiments/utils/featureFlags'
import NonAvailableVariantDetails from 'views/components/organisms/ProductVariants/ProductVariant/NonAvailableVariantDetails'
import ProductVariantAvailableDetails from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantAvailableDetails'
import ProductVariantAvailableDetailsRebranded from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantAvailableDetailsRebranded'
import ProductVariantWrapper from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantWrapper'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import {
  getVariantDiscount,
  getVariantPrices,
  isProductNotInAssortment,
  isProductOutOfStock,
  isProductOutOfTrade,
} from 'views/utils/productUtils'
import { ProductVariantType, VariantOfferType } from 'views/utils/propTypes'

import 'views/components/organisms/ProductVariants/ProductVariant/ProductVariant.scss'

const ProductVariant = ({
  checked,
  radioButton,
  groupName,
  isMasterPage,
  lowestPrice,
  variant: {
    unitQuantityString,
    unit,
    unitPricing,
    canonicalURL,
    id,
    followUpProductCode,
    discount,
    stockStatus,
    prices,
    trackingData,
    isMarketplaceProduct,
    path,
  },
  variantOffer,
  productTemplate,
  prescriptionType,
  isLast,
  withExtraMargin,
}) => {
  const isOutOfTrade = isProductOutOfTrade(stockStatus)
  const isOutOfStock = isProductOutOfStock(stockStatus)
  const isNotInAssortment = isProductNotInAssortment(stockStatus)
  const isNotLink = isOutOfTrade || checked
  const {
    publicRuntimeConfig: {
      pageProperties: { experiments = [] },
      publicConfig: { tenant },
    },
  } = useShopAndRuntimeConfig()

  const isVariantAttributeFlag = isExperimentVariableTrue(
    VARIANT_ATTRIBUTES,
    RELEASE_TOGGLE_VARIANT_ATTTRIBUTES_ENABLED,
    experiments
  )
  const variantProps = {
    productTemplate,
    prescriptionType,
    isOutOfTrade,
    isOutOfStock,
    prices: getVariantPrices({
      variantOffer,
      prices,
    }),
    discount: getVariantDiscount({
      variantOffer,
      discount,
      tenant,
    }),
    followUpProductCode,
    withExtraMargin,
    isMarketplaceProduct,
  }
  const nonAvailableVariantProps = {
    followUpProductCode,
    isOutOfTrade,
    isOutOfStock,
  }
  const wrapperProps = {
    radioButton,
    canonicalURL,
    id,
    groupName,
    checked,
    isOutOfTrade,
    isOutOfStock,
    isLast,
    isNotLink,
    trackingData,
    path,
  }

  return (
    <>
      {!isVariantAttributeFlag ? (
        <ProductVariantWrapper {...wrapperProps}>
          <div
            className={classNames(
              'm-ProductVariant__checkbox l-flex__primary',
              {
                'u-color--grey m-ProductVariant__checkbox--out-of-trade':
                  isOutOfTrade || isOutOfStock || isNotInAssortment,
              }
            )}
          >
            <div className="u-no-margin a-h2 u-font-weight--bold u-no-wrap o-ProductVariant--units">
              {unitQuantityString} {unit}
            </div>

            {!isOutOfTrade && (
              <div className="a-h6 u-no-margin u-font-weight--normal u-no-wrap o-ProductVariant--base-price">
                {unitPricing}
              </div>
            )}
          </div>

          {isOutOfTrade || isNotInAssortment ? (
            <NonAvailableVariantDetails {...nonAvailableVariantProps} />
          ) : (
            <ProductVariantAvailableDetails
              {...variantProps}
              isMasterPage={isMasterPage}
              lowestPrice={lowestPrice}
            />
          )}
        </ProductVariantWrapper>
      ) : (
        <>
          {isOutOfStock && !isMasterPage ? (
            <div className="a-h2 u-color--grey u-padding--left">
              <Trans id="general.label.outOfStock" />
            </div>
          ) : (
            <ProductVariantAvailableDetailsRebranded
              {...variantProps}
              isMasterPage={isMasterPage}
              lowestPrice={lowestPrice}
            />
          )}
        </>
      )}
    </>
  )
}

ProductVariant.propTypes = {
  checked: bool,
  radioButton: bool,
  groupName: string,
  variant: shape(ProductVariantType).isRequired,
  variantOffer: VariantOfferType,
  isLast: bool,
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES).isRequired,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  useOTCMode: bool,
  withExtraMargin: bool,
  isMasterPage: bool,
  lowestPrice: number,
}

ProductVariant.defaultProps = {
  checked: false,
  radioButton: false,
  groupName: 'packageVariant',
  isLast: false,
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  useOTCMode: false,
  withExtraMargin: false,
  isMasterPage: false,
}

export default ProductVariant
